import { useEffect, useState, type ReactNode } from 'react';
import { LayoutCanvas } from './canvas.js';
import { EditorSheet } from './EditorSheet.js';
import {
  CanvasSettings,
  IssueList,
  PlaylistList,
  PositionFields,
  RegionsList,
  WidgetDetail,
  WidgetTiles,
  checkTitle,
  regionTitle,
} from './panels.js';
import { moduleIcon, moduleLabel } from './widgets.js';
import type { LayoutEditorState } from './useLayoutEditor.js';

type SheetKind = 'content' | 'add' | 'position' | 'regions' | 'canvas' | 'check';

/** Sheets about the selected region; they close when nothing is selected. */
const REGION_SHEETS = new Set<SheetKind>(['content', 'add', 'position']);

/**
 * The phone editor, laid out like a design app: a full-screen canvas, a
 * context toolbar along the bottom that changes with the selection, and
 * half-height sheets for the details. Everything edits through `ed`.
 */
export function MobileEditor({ ed }: { ed: LayoutEditorState }): ReactNode {
  const { doc, history, issues, selectedIndex, setSelectedRegion } = ed;
  const [openSheet, setOpenSheet] = useState<SheetKind | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  if (!doc) return null;
  const region = selectedIndex >= 0 ? doc.regions[selectedIndex] : undefined;
  const ri = selectedIndex;
  const sheet = openSheet && REGION_SHEETS.has(openSheet) && !region ? null : openSheet;
  const items = region?.playlist.items ?? [];
  const wi = widgetId ? items.findIndex((w) => w.id === widgetId) : -1;
  const widget = wi >= 0 ? items[wi] : undefined;
  const close = (): void => setOpenSheet(null);

  function toggle(kind: SheetKind): void {
    setOpenSheet((s) => (s === kind ? null : kind));
    if (kind === 'content') setWidgetId(null);
  }

  function select(id: string | null): void {
    if (id !== ed.selectedRegion) setWidgetId(null);
    setSelectedRegion(id);
  }

  return (
    <div className="m-editor">
      <header className="m-editor-top">
        <button type="button" className="icon-btn" aria-label="Back to layouts" onClick={ed.leave}>
          ‹
        </button>
        <input
          className="m-editor-name"
          aria-label="Layout name"
          value={ed.name}
          onChange={(e) => ed.setName(e.target.value)}
        />
        <button type="button" className="icon-btn" aria-label="Undo" disabled={!history.canUndo} onClick={history.undo}>
          ↶
        </button>
        <button type="button" className="icon-btn" aria-label="Redo" disabled={!history.canRedo} onClick={history.redo}>
          ↷
        </button>
        <button
          type="button"
          className="btn small"
          disabled={ed.saving || (!ed.dirty && Boolean(ed.id))}
          onClick={() => void ed.save()}
        >
          {ed.saving ? '…' : ed.dirty || !ed.id ? 'Save' : 'Saved'}
        </button>
        <button
          type="button"
          className="btn small primary m-publish"
          disabled={ed.saving}
          onClick={() => (issues.length > 0 ? setOpenSheet('check') : void ed.publish())}
        >
          Publish
          {issues.length > 0 ? (
            <span className="m-badge" aria-label={`${issues.length} issues`}>
              {issues.length}
            </span>
          ) : null}
        </button>
      </header>

      <div className="m-editor-stage">
        <LayoutCanvas
          doc={doc}
          media={ed.media}
          selectedId={ed.selectedRegion}
          onSelect={select}
          onRectChange={(index, rect, key) => ed.patchRegion(index, { rect }, key)}
          onGestureStart={history.begin}
          onGestureEnd={history.end}
        />
      </div>

      {sheet === 'content' && region ? (
        widget ? (
          <EditorSheet
            title={`${moduleIcon(widget.module)} ${moduleLabel(widget.module, ed.moduleBySlug.get(widget.module))}`}
            onBack={() => setWidgetId(null)}
            onClose={close}
          >
            <WidgetDetail ed={ed} ri={ri} wi={wi} onDeleted={() => setWidgetId(null)} />
          </EditorSheet>
        ) : (
          <EditorSheet title={`${regionTitle(region, ri)} · playlist`} onClose={close}>
            <PlaylistList ed={ed} ri={ri} onOpen={setWidgetId} onAdd={() => setOpenSheet('add')} />
          </EditorSheet>
        )
      ) : null}

      {sheet === 'add' && region ? (
        <EditorSheet title={`Add to ${regionTitle(region, ri)}`} onClose={close}>
          <WidgetTiles
            ed={ed}
            onPick={(m) => {
              setWidgetId(ed.addWidget(ri, m));
              setOpenSheet('content');
            }}
          />
        </EditorSheet>
      ) : null}

      {sheet === 'position' && region ? (
        <EditorSheet title={`${regionTitle(region, ri)} · position`} onClose={close}>
          <PositionFields ed={ed} ri={ri} />
        </EditorSheet>
      ) : null}

      {sheet === 'regions' ? (
        <EditorSheet title="Regions" onClose={close}>
          <RegionsList ed={ed} onSelect={select} />
        </EditorSheet>
      ) : null}

      {sheet === 'canvas' ? (
        <EditorSheet title="Canvas" onClose={close}>
          <CanvasSettings ed={ed} />
        </EditorSheet>
      ) : null}

      {sheet === 'check' ? (
        <EditorSheet title={checkTitle(issues)} onClose={close}>
          <IssueList issues={issues} />
        </EditorSheet>
      ) : null}

      <nav className="m-toolbar" aria-label="Editor tools">
        {region ? (
          <>
            <Tool icon="▤" label="Playlist" active={sheet === 'content'} onClick={() => toggle('content')} />
            <Tool icon="＋" label="Widget" active={sheet === 'add'} onClick={() => toggle('add')} />
            <Tool icon="⤢" label="Position" active={sheet === 'position'} onClick={() => toggle('position')} />
            <Tool icon="⧉" label="Copy" onClick={() => ed.duplicateRegion(ri)} />
            <Tool
              icon="🗑"
              label="Delete"
              danger
              disabled={doc.regions.length <= 1}
              onClick={() => ed.removeRegion(ri)}
            />
            <Tool icon="✓" label="Done" onClick={() => select(null)} />
          </>
        ) : (
          <>
            <Tool icon="＋" label="Region" onClick={ed.addRegion} />
            <Tool icon="☰" label="Regions" active={sheet === 'regions'} onClick={() => toggle('regions')} />
            <Tool icon="▭" label="Canvas" active={sheet === 'canvas'} onClick={() => toggle('canvas')} />
            <Tool
              icon={issues.length ? '!' : '✓'}
              label="Check"
              badge={issues.length}
              active={sheet === 'check'}
              onClick={() => toggle('check')}
            />
          </>
        )}
      </nav>
    </div>
  );
}

/** A labelled icon button for the editor's tool rail/bar, with an optional count badge. */
export function Tool({
  icon,
  label,
  onClick,
  active,
  disabled,
  danger,
  badge,
  className = 'm-tool',
}: {
  icon: string;
  label: string;
  onClick: () => void;
  /** Set for tools that open a panel: whether that panel is open. */
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  badge?: number;
  className?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className={`${className}${active ? ' active' : ''}${danger ? ' danger' : ''}`}
      aria-pressed={active}
      disabled={disabled}
      title={label}
      onClick={onClick}
    >
      <span className="m-tool-icon" aria-hidden>
        {icon}
      </span>
      <span>{label}</span>
      {badge ? <span className="m-badge">{badge}</span> : null}
    </button>
  );
}
